/**
 * Actions: who does what by when, carried forward until closed. An action can
 * be raised from a decision, a lever, a billing gap (and, as they're built,
 * risks, obligations and meetings), and stays linked to it.
 *
 * It's due on a date, on a milestone (go-live, the next board) whose date fills
 * in when it's known, or not yet. Every move of its due date is kept with a
 * reason. Closing it takes a note or a link; whoever raised it can confirm it.
 */
import { addDays, type ISODate } from "./dates";

export type ActionSourceKind = "decision" | "lever" | "billing" | "risk" | "obligation" | "release" | "meeting";
export const ACTION_SOURCE_LABEL: Record<ActionSourceKind, string> = { decision: "Decision", lever: "Lever", billing: "Billing", risk: "Risk", obligation: "Obligation", release: "Release", meeting: "Meeting" };

export interface ActionSource {
  kind: ActionSourceKind;
  id: string;
  /** What it was called when the action was raised, kept if it goes. */
  label: string;
}

export interface Milestone {
  id: string;
  name: string;
  /** Null until it's known. */
  date: ISODate | null;
  active: boolean;
  sortOrder: number;
}

/** A change to when an action is due. Dates are what it was due on, worked out from any milestone. */
export interface DueChange {
  id: string;
  fromDate: ISODate | null;
  fromMilestoneId: string | null;
  toDate: ISODate | null;
  toMilestoneId: string | null;
  reason: string;
  /** Its milestone moved, rather than the action. */
  viaMilestone: boolean;
  by: string | null;
  at: string;
}

export interface Action {
  id: string;
  ref: number;
  title: string;
  notes: string;
  ownerId: string | null;
  /** Due on a date, or on a milestone (then `due` is null). */
  due: ISODate | null;
  milestoneId: string | null;
  /** The first date it was due on, kept as it moves. */
  firstDue: ISODate | null;
  source: ActionSource | null;
  status: "open" | "done";
  doneAt: string | null;
  doneBy: string | null;
  doneNote: string | null;
  doneLink: string | null;
  checkedBy: string | null;
  checkedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  changes: DueChange[];
}

export const actionRef = (n: number) => `A-${String(n).padStart(3, "0")}`;

/** When it's due: its date, or its milestone's (null while the milestone has none). */
export function dueOf(a: Pick<Action, "due" | "milestoneId">, milestones: Pick<Milestone, "id" | "date">[]): ISODate | null {
  if (a.milestoneId) return milestones.find((m) => m.id === a.milestoneId)?.date ?? null;
  return a.due;
}

export type ActionState = "overdue" | "due_soon" | "open" | "done" | "checked";
export const ACTION_STATE_LABEL: Record<ActionState, string> = { overdue: "Overdue", due_soon: "Due soon", open: "Open", done: "Done", checked: "Done, checked" };

/** Worked out from the dates, never typed: overdue the day after it was due. */
export function actionState(a: Pick<Action, "status" | "checkedAt">, due: ISODate | null, today: ISODate, soonDays: number): ActionState {
  if (a.status === "done") return a.checkedAt ? "checked" : "done";
  if (!due) return "open";
  if (due < today) return "overdue";
  if (due <= addDays(today, soonDays)) return "due_soon";
  return "open";
}

/** How many times its due date has moved (setting the first one isn't a move). */
export const timesMoved = (a: Pick<Action, "changes">) => a.changes.filter((c) => c.fromDate !== null && c.toDate !== c.fromDate).length;

/** Done, but not confirmed by whoever raised it (unless they closed it themselves). */
export const awaitingCheck = (a: Pick<Action, "status" | "checkedAt" | "createdBy" | "doneBy">) => a.status === "done" && !a.checkedAt && !!a.createdBy && a.createdBy !== a.doneBy;
